import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment-timezone';

import { PageHeader } from '../../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../../components/PageHeader/Button';
import Table3, { columnHelper } from '../../../../containers/Table/Table3';
import SimpleModal from '../../../../containers/Modal/SimpleModal';
import Select from '../../../../components/Select';
import { notificationActions } from '../../../../components/Notification/redux/actions';
import { openModalAction } from '../../../../actions/modal';
import NewConfirmationModal from '../../../../modals/NewConfirmationModal/NewConfirmationModal';
import { GET_MY_ORGS_RESULT } from '../../../../components/OrgSelect/orgFilterActions';
import Strings from '../../../../Strings';
import { DATE_MONTH_DAY_YEAR, DATE_TIME_SPACE_12_UPPERCASE } from '../../../../constants';
import { getStoredOrgUUID, storeOrgUUID } from '../useAccessOrgSelection';
import accessStyles from '../AccessSimulator/AccessSimulator.module.scss';
import {
  actions,
  GET_NPPES_SIM_COMM_LOG_RESULT,
  GET_NPPES_SIM_CONFIG_RESULT,
  LIST_NPPES_SIM_PROVIDERS_RESULT,
} from './actions';
import ProviderModal, { emptyProviderDraft, providerDraftFrom, providerDraftIsValid, providerFromDraft } from './ProviderModal';

const FAILURE_OPTIONS = [
  { value: null, label: Strings.nppesSimAnswerNormally },
  ...[400, 401, 403, 429, 500, 502, 503].map(status => ({ value: status, label: `HTTP ${status}` })),
];

const bodyPreview = body => {
  const oneLine = (body || '').replace(/\s+/g, ' ').trim();
  if (!oneLine) return '-';
  return oneLine.length > 300 ? `${oneLine.slice(0, 300)}…` : oneLine;
};

const providerName = row => (row.enumeration_type === 'NPI-2'
  ? row.organization_name
  : [row.last_name, row.first_name].filter(Boolean).join(', '));

/**
 * Superuser tab driving the NPI Registry simulator: pointing the org's NPPES
 * calls at it, the global simulated registry it serves, failure injection and
 * the request log. The registry, the failure setting and the log are global —
 * like the real registry, one shared service — while pointing is per org.
 */
function NppesSimulator({
  userId, getMyOrgs, getConfig, listProviders, getCommLog, pointOrg, unpointOrg, setSettings,
  addSampleData, reset, saveProvider, deleteProvider, showNotification, openConfirmModal,
}) {
  const [orgs, setOrgs] = useState([]);
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  const [orgUUID, setOrgUUID] = useState(null);
  const [config, setConfig] = useState(null);
  const [providers, setProviders] = useState([]);
  const [commLog, setCommLog] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [providerModal, setProviderModal] = useState(null);

  const showError = resp => showNotification(
    resp?.response?.data?.standard_error?.message || Strings.errors.internalError,
    5000,
    true,
  );

  // loadGeneration bumps on every load so a slow response from an org the
  // user already switched away from is discarded instead of repopulating the
  // tab with the previous organization's config.
  const loadGeneration = useRef(0);

  const load = useCallback(targetOrgUUID => {
    const generation = loadGeneration.current + 1;
    loadGeneration.current = generation;
    setIsLoading(true);
    return Promise.all([getConfig(targetOrgUUID), listProviders(), getCommLog()]).then(([configResp, providersResp, logResp]) => {
      if (generation !== loadGeneration.current) return;
      if (configResp.type === GET_NPPES_SIM_CONFIG_RESULT) {
        setConfig(configResp.response);
      } else {
        setConfig(null);
        showError(configResp);
      }
      setProviders(providersResp.type === LIST_NPPES_SIM_PROVIDERS_RESULT ? providersResp.response.providers : []);
      setCommLog(logResp.type === GET_NPPES_SIM_COMM_LOG_RESULT ? logResp.response.entries : []);
      setIsLoading(false);
    });
  }, [getConfig, listProviders, getCommLog]);

  useEffect(() => {
    getMyOrgs().then(resp => {
      if (resp.type === GET_MY_ORGS_RESULT) {
        const data = (resp.response?.data || []).map(org => ({ value: org.masked_id, label: org.name }));
        setOrgs(data);
        // The org picked on the ACCESS simulator tab is shared: the
        // simulators normally serve the same test organization.
        const stored = getStoredOrgUUID(userId);
        if (stored && data.some(org => org.value === stored)) setOrgUUID(stored);
      } else {
        showError(resp);
      }
    }).finally(() => setOrgsLoaded(true));
  }, [getMyOrgs, userId]);

  // Nothing to show until an organization is picked (a superuser usually has
  // none of their own).
  useEffect(() => {
    if (!orgsLoaded) return;
    if (orgUUID) load(orgUUID);
    else setIsLoading(false);
  }, [orgsLoaded, orgUUID, load]);

  const selectOrg = option => {
    const value = option?.value ?? null;
    storeOrgUUID(userId, value);
    // Drop the previous organization's config right away: the reload below is
    // async, and keeping the old state visible would invite pointing the
    // wrong org.
    setConfig(null);
    setOrgUUID(value);
  };

  // run performs a mutating call, reports the outcome and reloads the tab.
  // It resolves to whether the call succeeded. Mutations are off while a
  // load is in flight too: the table may still show stale rows.
  const run = (call, successMessage) => {
    if (isSaving || isLoading) return Promise.resolve(false);
    setIsSaving(true);
    return call().then(resp => {
      const ok = resp.type.endsWith('/result');
      if (!ok) showError(resp);
      else if (successMessage) showNotification(successMessage, 5000);
      return load(orgUUID).then(() => ok);
    }).finally(() => setIsSaving(false));
  };

  const settings = config?.settings || {};

  const changeSetting = value => run(() => setSettings({ api_http_status: value }));

  const saveProviderModal = () => run(
    () => saveProvider(providerModal.provider?.id, providerFromDraft(providerModal.draft)),
  ).then(ok => ok && setProviderModal(null));

  const confirmRun = (text, call) => openConfirmModal({ text, onConfirm: () => run(call) });

  const providerColumns = [
    columnHelper.accessor('npi', { header: Strings.nppesSimNpi, size: 120 }),
    columnHelper.accessor('enumeration_type', {
      header: Strings.nppesSimEnumerationType,
      size: 120,
      cell: info => (info.getValue() === 'NPI-2' ? Strings.nppesSimEnumOrganization : Strings.nppesSimEnumIndividual),
    }),
    columnHelper.accessor('last_name', { header: Strings.name, size: 220, cell: info => providerName(info.row.original) || '-' }),
    columnHelper.accessor('credential', { header: Strings.nppesSimCredential, size: 100, cell: info => info.getValue() || '-' }),
    columnHelper.accessor('status', {
      header: Strings.nppesSimStatus,
      size: 120,
      cell: info => (info.getValue() === 'D' ? Strings.nppesSimStatusDeactivated : Strings.nppesSimStatusActive),
    }),
    columnHelper.accessor('last_updated', {
      header: Strings.nppesSimLastUpdated,
      size: 130,
      cell: info => (info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-'),
    }),
  ];

  const logColumns = [
    columnHelper.accessor('created_at', {
      header: Strings.nppesSimTime,
      size: 190,
      cell: info => moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE),
    }),
    columnHelper.accessor('path', {
      header: Strings.nppesSimRequest,
      size: 420,
      enableSorting: false,
      cell: info => `${info.row.original.method} ${info.getValue()}${info.row.original.query ? `?${info.row.original.query}` : ''}`,
    }),
    columnHelper.accessor('http_status', { header: Strings.nppesSimHttpStatus, size: 90 }),
    columnHelper.accessor('response_body', {
      header: Strings.nppesSimResponseBody, size: 520, enableSorting: false, cell: info => bodyPreview(info.getValue()),
    }),
  ];

  const noOrg = !config;

  return (
    <>
      <PageHeader
        left={Strings.nppesSimulatorTab}
        right={(
          <>
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.nppesSimReset}
              onClick={() => confirmRun(Strings.nppesSimResetConfirm, () => reset())}
              disabled={isSaving}
              testid="reset-nppes-simulator"
            />
            {config?.pointed_at_simulator ? (
              <Button
                class={HEADER_BUTTON_DARK_BLUE}
                title={Strings.nppesSimUnpointOrg}
                onClick={() => confirmRun(Strings.nppesSimUnpointOrgConfirm, () => unpointOrg(orgUUID))}
                disabled={isSaving || noOrg}
                testid="unpoint-nppes-simulator"
              />
            ) : (
              <Button
                class={HEADER_BUTTON_DARK_BLUE}
                title={Strings.nppesSimPointOrg}
                tooltip={config && !config.key_configured ? Strings.nppesSimKeyMissingTooltip : undefined}
                onClick={() => confirmRun(
                  Strings.formatString(Strings.nppesSimPointOrgConfirm, orgs.find(org => org.value === orgUUID)?.label || orgUUID),
                  () => pointOrg(orgUUID),
                )}
                disabled={isSaving || noOrg}
                testid="point-nppes-simulator"
              />
            )}
          </>
        )}
      />

      <div className={accessStyles.accessSimulator}>
        <div className={accessStyles.accessSimulatorOrg}>
          <span className={accessStyles.accessSimulatorFieldLabel}>{Strings.accessSimOrg}</span>
          <Select id="nppes-simulator-org" data={orgs} value={orgUUID} onChange={selectOrg} isSearchable disabled={isSaving || isLoading} />
        </div>
        <h2>{config?.pointed_at_simulator ? Strings.nppesSimPointed : Strings.nppesSimNotPointed}</h2>
        <div className={accessStyles.accessSimulatorStatus}>
          <div className={accessStyles.accessSimulatorField}>
            {Strings.nppesSimBaseUrl}
            <span className={accessStyles.accessSimulatorValue}>{config?.base_url || Strings.accessSimNotConfigured}</span>
          </div>
          <div className={accessStyles.accessSimulatorField}>
            <span data-tooltip-id="tooltip" data-tooltip-content={Strings.nppesSimKeyTooltip}>{Strings.nppesSimKey}</span>
            <span className={accessStyles.accessSimulatorValue}>
              {config?.key_configured ? config?.api_key || Strings.nppesSimKeyConfigured : Strings.nppesSimKeyMissing}
            </span>
          </div>
          <div className={accessStyles.accessSimulatorField}>
            {Strings.nppesSimApiFailure}
            <Select
              id="nppes-simulator-api-failure"
              data={FAILURE_OPTIONS}
              value={settings.api_http_status ?? null}
              onChange={option => changeSetting(option?.value ?? null)}
              disabled={isSaving}
            />
          </div>
        </div>
        <p className={accessStyles.accessSimulatorNote}>
          {`${Strings.nppesSimNote} ${config?.simulator_url || ''}`}
        </p>
      </div>

      <div className={accessStyles.accessSimulatorTable}>
        <div className={accessStyles.accessSimulatorSectionHeader}>
          <h2>{Strings.nppesSimProviders}</h2>
          <div className={accessStyles.accessSimulatorSectionActions}>
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.nppesSimAddSampleData}
              tooltip={Strings.nppesSimAddSampleDataTooltip}
              onClick={() => run(() => addSampleData(), Strings.nppesSimSampleDataAdded)}
              disabled={isSaving}
              testid="nppes-sim-sample-data"
            />
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.nppesSimAddProvider}
              onClick={() => setProviderModal({ provider: null, draft: emptyProviderDraft() })}
              disabled={isSaving}
              testid="add-nppes-sim-provider"
            />
          </div>
        </div>
        <Table3
          name="nppes-simulator-providers"
          data={providers}
          columns={providerColumns}
          isLoading={isLoading}
          noPagination
          buttons={[
            { icon: 'edit', onClick: (rowId, provider) => setProviderModal({ provider, draft: providerDraftFrom(provider) }), testId: 'edit-nppes-sim-provider' },
            { icon: 'delete', onClick: (rowId, provider) => confirmRun(Strings.nppesSimDeleteConfirm, () => deleteProvider(provider.id)), testId: 'delete-nppes-sim-provider' },
          ]}
        />
      </div>

      <div className={accessStyles.accessSimulatorTable}>
        <div className={accessStyles.accessSimulatorSectionHeader}>
          <h2>{Strings.nppesSimRequestLog}</h2>
          <div className={accessStyles.accessSimulatorSectionActions}>
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.nppesSimRefresh}
              onClick={() => load(orgUUID)}
              disabled={isLoading}
              testid="refresh-nppes-sim-log"
            />
          </div>
        </div>
        <Table3 name="nppes-simulator-log" data={commLog} columns={logColumns} isLoading={isLoading} />
      </div>

      {providerModal && (
        <SimpleModal
          name="nppes-simulator-provider"
          title={providerModal.provider ? Strings.nppesSimEditProvider : Strings.nppesSimAddProvider}
          onCancel={() => setProviderModal(null)}
          onSubmit={saveProviderModal}
          isSubmitDisabled={isSaving || !providerDraftIsValid(providerModal.draft)}
          content={(
            <ProviderModal
              draft={providerModal.draft}
              setDraft={draft => setProviderModal({ ...providerModal, draft })}
              isSaving={isSaving}
            />
          )}
        />
      )}
    </>
  );
}

NppesSimulator.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([undefined, null])]),
  getMyOrgs: PropTypes.func.isRequired,
  getConfig: PropTypes.func.isRequired,
  listProviders: PropTypes.func.isRequired,
  getCommLog: PropTypes.func.isRequired,
  pointOrg: PropTypes.func.isRequired,
  unpointOrg: PropTypes.func.isRequired,
  setSettings: PropTypes.func.isRequired,
  addSampleData: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  saveProvider: PropTypes.func.isRequired,
  deleteProvider: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getMyOrgs: () => dispatch(actions.getMyOrgs()),
  getConfig: orgUUID => dispatch(actions.getConfig(orgUUID)),
  listProviders: () => dispatch(actions.listProviders()),
  getCommLog: () => dispatch(actions.getCommLog()),
  pointOrg: orgUUID => dispatch(actions.pointOrg(orgUUID)),
  unpointOrg: orgUUID => dispatch(actions.unpointOrg(orgUUID)),
  setSettings: settings => dispatch(actions.setSettings(settings)),
  addSampleData: () => dispatch(actions.addSampleData()),
  reset: () => dispatch(actions.reset()),
  saveProvider: (id, provider) => dispatch(actions.saveProvider(id, provider)),
  deleteProvider: id => dispatch(actions.deleteProvider(id)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  openConfirmModal: data => dispatch(openModalAction(NewConfirmationModal.MODAL_NAME, data)),
});

const mapStateToProps = state => ({
  userId: state.auth?.profile?.id,
});

export default connect(mapStateToProps, mapDispatchToProps)(NppesSimulator);
